import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Button,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  Stack,
  Tabs,
  Tab,
  FormControlLabel,
} from '@mui/material';
import IconPicker from '../../components/IconPicker';
import { getSidebarIcon } from '../../icons/sidebarIcons';

/* -------------------------
   Tabs a11y helpers
------------------------- */
function a11yProps(index) {
  return {
    id: `menu-designer-tab-${index}`,
    'aria-controls': `menu-designer-tabpanel-${index}`,
  };
}
function TabPanel({ children, value, index }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`menu-designer-tabpanel-${index}`}
      aria-labelledby={`menu-designer-tab-${index}`}
      style={{ width: '100%' }}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

/* =========================
   API base (بدون /api)
========================= */
const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
const apiUrl = (p) => `${API_BASE}${p}`;

/* Helper: JSON fetch برسائل خطأ واضحة */
async function fetchJSON(url, options = {}) {
  const token = localStorage.getItem('token') || '';
  const res = await fetch(url, {
    credentials: 'include',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  const ct = res.headers.get('content-type') || '';

  if (!res.ok) {
    if (ct.includes('application/json')) {
      const err = await res.json().catch(() => ({}));
      const msg =
        err?.details || err?.message || res.statusText || `HTTP ${res.status}`;
      throw new Error(msg);
    }
    const text = await res.text().catch(() => '');
    throw new Error(
      `HTTP ${res.status} — non-JSON response: ${text.slice(0, 120)}…`
    );
  }

  if (!ct.includes('application/json')) {
    const text = await res.text().catch(() => '');
    throw new Error(
      `Expected JSON but got ${ct || 'unknown'}: ${text.slice(0, 120)}…`
    );
  }

  return res.json();
}

/* -------------------------
   API: MENU
   GET    /app-menu
   POST   /app-menu
   PUT    /app-menu/:id
   DELETE /app-menu/:id
------------------------- */
async function apiFetchMenu() {
  const data = await fetchJSON(apiUrl('/app-menu'));
  const arr = Array.isArray(data) ? data : data ? [data] : [];
  return arr.map((it) => ({
    menu_id: it.menu_id,
    text_key: it.text_key,
    path: it.path,
    icon_key: it.icon_key,
    permission_key: it.permission_key,
    sort_order: it.sort_order ?? 0,
    is_active: !!it.is_active,
    parent_id: it.parent_id ?? null,
  }));
}
async function apiCreateMenu(item) {
  const body = {
    text_key: item.text_key,
    path: item.path,
    icon_key: item.icon_key || 'default',
    permission_key: item.permission_key || item.text_key?.trim().toLowerCase(),
    sort_order: Number(item.sort_order ?? 0),
    is_active: item.is_active ? 1 : 0,
    parent_id: item.parent_id ?? null,
  };
  return fetchJSON(apiUrl('/app-menu'), {
    method: 'POST',
    body: JSON.stringify(body),
  });
}
async function apiUpdateMenu(menu_id, item) {
  const body = {
    text_key: item.text_key,
    path: item.path,
    icon_key: item.icon_key || 'default',
    permission_key: item.permission_key || item.text_key?.trim().toLowerCase(),
    sort_order: Number(item.sort_order ?? 0),
    is_active: item.is_active ? 1 : 0,
    parent_id: item.parent_id ?? null,
  };
  return fetchJSON(apiUrl(`/app-menu/${menu_id}`), {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}
async function apiDeleteMenu(menu_id) {
  return fetchJSON(apiUrl(`/app-menu/${menu_id}`), { method: 'DELETE' });
}

/* -------------------------
   API: ROLES
   GET /roles
------------------------- */
async function apiFetchRoles() {
  const data = await fetchJSON(apiUrl('/roles'));
  const arr = Array.isArray(data) ? data : data ? [data] : [];
  return arr.map((r) => ({
    role_id: r.role_id,
    name: r.role_name_ar || r.role_name || `Role ${r.role_id}`,
  }));
}

/* -------------------------
   API: Role → Menu Access
   GET  /role-menu            => صف واحد أو مصفوفة: { role_id, menu_id, allowed? }
   POST /role-menu/toggle     { role_id, menu_id, allow }
------------------------- */
async function apiFetchRoleAccess() {
  const data = await fetchJSON(apiUrl('/role-menu'));
  const rows = Array.isArray(data) ? data : data ? [data] : [];

  const out = {};
  rows.forEach((row) => {
    if (!row || row.role_id == null || row.menu_id == null) return;
    const rid = String(row.role_id);
    if (!out[rid]) out[rid] = new Set();

    const allowedField =
      row.allowed ?? row.is_allowed ?? row.allow ?? row.permit ?? 1;
    const isAllowed =
      allowedField === 1 || allowedField === true || allowedField === '1';

    if (isAllowed) out[rid].add(row.menu_id);
  });

  return out;
}
async function apiToggleRoleAccess(role_id, menu_id, allow) {
  return fetchJSON(apiUrl('/role-menu/toggle'), {
    method: 'POST',
    body: JSON.stringify({ role_id, menu_id, allow }),
  });
}

/* =========================================================
   Component
========================================================= */
export default function MenuDesigner() {
  const [menu, setMenu] = useState([]);
  const [roles, setRoles] = useState([]);
  const [access, setAccess] = useState({}); // { [role_id]: Set(menu_id) }
  const [editing, setEditing] = useState(null);
  const [iconOpen, setIconOpen] = useState(false);
  const [tab, setTab] = useState(() => {
    const saved = localStorage.getItem('menuDesignerTabIndex');
    return saved ? Number(saved) : 0;
  });

  useEffect(() => {
    (async () => {
      try {
        const [m, r, a] = await Promise.all([
          apiFetchMenu(),
          apiFetchRoles(),
          apiFetchRoleAccess(),
        ]);

        const ensured =
          m && m.length
            ? m
            : [
                {
                  text_key: 'home',
                  path: '/home',
                  icon_key: 'home',
                  permission_key: 'home',
                  sort_order: 0,
                  is_active: 1,
                },
              ];

        setMenu(ensured);
        setRoles(r);
        setAccess(a);
      } catch (e) {
        alert(`فشل تحميل البيانات: ${e.message}`);
      }
    })();
  }, []);

  useEffect(() => {
    localStorage.setItem('menuDesignerTabIndex', String(tab));
  }, [tab]);

  const normalizePath = (p = '') => {
    p = String(p || '').trim();
    if (!p.startsWith('/')) p = '/' + p;
    return p.replace(/\/+/, '/').toLowerCase();
  };
  const normalizeKey = (s = '') =>
    String(s || '')
      .trim()
      .toLowerCase();

  const sortedMenu = useMemo(
    () => [...menu].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
    [menu]
  );

  // CRUD
  const startAdd = () => {
    setEditing({
      menu_id: undefined,
      text_key: '',
      path: '',
      icon_key: 'home',
      permission_key: '',
      sort_order: menu.length
        ? Math.max(...menu.map((i) => i.sort_order ?? 0)) + 1
        : 1,
      is_active: 1,
      parent_id: null,
      _original_path: null,
    });
    setTab(0);
  };
  const startEdit = (item) => {
    setEditing({ ...item, _original_path: item.path || null });
    setTab(0);
  };
  const cancelEdit = () => setEditing(null);

  const saveEdit = async () => {
    const clean = { ...editing };
    clean.path = normalizePath(clean.path);
    clean.text_key = (clean.text_key || '').trim();
    clean.permission_key =
      normalizeKey(clean.permission_key || '') || normalizeKey(clean.text_key);
    clean.icon_key = (
      clean.icon_key ||
      clean.permission_key ||
      'default'
    ).trim();
    clean.is_active = clean.is_active ? 1 : 0;

    if (!clean.text_key || !clean.path) {
      alert('الاسم والمسار إلزاميان');
      return;
    }

    const clash = menu
      .filter((it) => it.menu_id !== clean.menu_id)
      .some((it) => normalizePath(it.path) === clean.path);
    if (clash) {
      alert('المسار مستخدم من عنصر آخر');
      return;
    }

    try {
      if (clean.menu_id) {
        await apiUpdateMenu(clean.menu_id, clean);
      } else {
        const created = await apiCreateMenu(clean);
        clean.menu_id = created?.menu_id ?? clean.menu_id;
      }
      const fresh = await apiFetchMenu();
      setMenu(fresh);
      setEditing(null);
    } catch (e) {
      alert(`فشل الحفظ: ${e.message}`);
    }
  };

  const removeItem = async (menu_id) => {
    if (!menu_id) return;
    if (!window.confirm('تأكيد الحذف؟')) return;
    try {
      await apiDeleteMenu(menu_id);
      const fresh = await apiFetchMenu();
      setMenu(fresh);
      const newAccess = { ...access };
      Object.keys(newAccess).forEach((rid) => newAccess[rid].delete(menu_id));
      setAccess(newAccess);
    } catch (e) {
      alert(`فشل الحذف: ${e.message}`);
    }
  };

  // Role access
  const toggleRoleAccess = async (role_id, menu_id) => {
    const next = { ...access };
    if (!next[role_id]) next[role_id] = new Set();
    const willAllow = !next[role_id].has(menu_id);
    try {
      await apiToggleRoleAccess(role_id, menu_id, willAllow);
      if (willAllow) next[role_id].add(menu_id);
      else next[role_id].delete(menu_id);
      setAccess(next);
    } catch (e) {
      alert(`تعذر تحديث الصلاحية: ${e.message}`);
    }
  };

  // Render
  return (
    <Box sx={{ p: 2 }}>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons
        allowScrollButtonsMobile
      >
        <Tab label="عناصر القائمة" {...a11yProps(0)} />
        <Tab label="صلاحيات الأدوار" {...a11yProps(1)} />
      </Tabs>

      {/* Tab 0 */}
      <TabPanel value={tab} index={0}>
        <Card>
          <CardHeader
            title="عناصر القائمة الجانبية"
            action={
              <Button variant="contained" onClick={startAdd}>
                عنصر جديد
              </Button>
            }
          />
          <CardContent>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ترتيب</TableCell>
                  <TableCell>أيقونة</TableCell>
                  <TableCell>النص (text_key)</TableCell>
                  <TableCell>المسار (path)</TableCell>
                  <TableCell>permission_key</TableCell>
                  <TableCell align="right">إجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedMenu.map((m) => {
                  const Icon = getSidebarIcon(m);
                  return (
                    <TableRow key={m.menu_id ?? m.path}>
                      <TableCell width={70}>{m.sort_order ?? 0}</TableCell>
                      <TableCell width={60}>
                        <Icon
                          style={{ width: 20, height: 20, color: '#4b4843' }}
                        />
                      </TableCell>
                      <TableCell>{m.text_key}</TableCell>
                      <TableCell>{m.path}</TableCell>
                      <TableCell>{m.permission_key || '-'}</TableCell>
                      <TableCell align="right">
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="flex-end"
                        >
                          <Button size="small" onClick={() => startEdit(m)}>
                            تعديل
                          </Button>
                          {m.menu_id && (
                            <Button
                              size="small"
                              color="error"
                              onClick={() => removeItem(m.menu_id)}
                            >
                              حذف
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {/* Inline editor */}
            {editing && (
              <Box
                sx={{ mt: 2, p: 2, border: '1px dashed #ddd', borderRadius: 2 }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="text_key (لافتة الترجمة)"
                      fullWidth
                      value={editing.text_key}
                      onChange={(e) =>
                        setEditing((s) => ({ ...s, text_key: e.target.value }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="path (/home, /horse-list ...)"
                      fullWidth
                      value={editing.path}
                      onChange={(e) =>
                        setEditing((s) => ({ ...s, path: e.target.value }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="permission_key (مطلوب)"
                      fullWidth
                      placeholder="مثال: horses, statistics..."
                      value={editing.permission_key || ''}
                      onChange={(e) =>
                        setEditing((s) => ({
                          ...s,
                          permission_key: e.target.value,
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="sort_order (رقم الترتيب)"
                      type="number"
                      fullWidth
                      value={editing.sort_order ?? 0}
                      onChange={(e) =>
                        setEditing((s) => ({
                          ...s,
                          sort_order: Number(e.target.value),
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="icon_key"
                      fullWidth
                      value={editing.icon_key}
                      onClick={() => setIconOpen(true)}
                      onChange={() => {}}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!editing.is_active}
                          onChange={(e) =>
                            setEditing((s) => ({
                              ...s,
                              is_active: e.target.checked ? 1 : 0,
                            }))
                          }
                        />
                      }
                      label="نشط (is_active)"
                    />
                  </Grid>
                </Grid>
                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                  <Button variant="contained" onClick={saveEdit}>
                    حفظ
                  </Button>
                  <Button onClick={cancelEdit}>إلغاء</Button>
                </Stack>
              </Box>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Tab 1 */}
      <TabPanel value={tab} index={1}>
        <Card>
          <CardHeader title="(Role → Menu Access)" />
          <CardContent>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>المسار</TableCell>
                  {roles.map((r) => (
                    <TableCell key={r.role_id} align="center">
                      {r.name}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedMenu.map((m) => (
                  <TableRow key={m.menu_id ?? m.path}>
                    <TableCell>{m.path}</TableCell>
                    {roles.map((r) => {
                      const checked = !!access[r.role_id]?.has(m.menu_id);
                      return (
                        <TableCell
                          key={`${r.role_id}-${m.menu_id}`}
                          align="center"
                        >
                          <Checkbox
                            checked={!!checked}
                            onChange={() =>
                              toggleRoleAccess(r.role_id, m.menu_id)
                            }
                            disabled={!m.menu_id}
                          />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Icon picker dialog */}
      <IconPicker
        open={iconOpen}
        value={editing?.icon_key}
        onClose={() => setIconOpen(false)}
        onSelect={(key) => setEditing((s) => ({ ...s, icon_key: key }))}
      />
    </Box>
  );
}
